/**
 * `<caos-tab-strip>` — horizontal facets of one record or configuration object.
 * (Catalog: Views & filtering › TabStrip — horizontal facets of one record or configuration object.)
 *
 * A real ARIA tablist: a `role="tablist"` bar of `role="tab"` buttons with an
 * accent underline indicator on the active tab (the design-system "active tab carries
 * the accent", which moves under a `data-brand` override). Tabs come from the `items`
 * property (`{ value, label, disabled? }[]` or plain strings) or declarative
 * `<button>` / `<option>` children. Keyboard: a roving tabindex — only the active tab
 * is tabbable; Arrow Left/Right/Home/End move focus and activate the tab (automatic
 * activation, the WAI-ARIA tabs pattern), skipping disabled tabs.
 *
 * Optional panels, said either way: a light-DOM child in the tab's own slot — `slot="panel-details"`
 * for the tab whose value is `details` — or a child in the shared `slot="panel"` carrying a matching
 * `data-value`. Either is wired as `role="tabpanel"`: the active tab's panel shows (labelled by its
 * tab, `aria-controls`/`aria-labelledby`), the rest are hidden. The per-tab slot exists because a
 * stored placement carries a slot name and cannot carry a `data-value` nobody declares, so it is the
 * only one of the two a page assembled from metadata can write (CAOS-1572). A bubbling, composed
 * `change` fires on selection. Styling seams: `::part(tablist)`, `::part(tab)`, `::part(panels)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import { applyAccessibleName } from '../accessible-name.js';

export interface CaosTabItem {
  value: string;
  label: string;
  disabled?: boolean;
}

let uid = 0;

/**
 * CAOS-1572 — the slot one tab's panels go into: the tab's own value, prefixed.
 *
 * Derived from the value rather than authored beside it, so there is no second list to keep in step
 * with the tabs and no way for a page to name a slot for a tab that does not exist.
 */
const panelSlotName = (value: string): string => `panel-${value}`;

/** Whether a `slot` attribute names one of the panel slots — the shared one, or a tab's own. */
const isPanelSlot = (name: string | null): boolean =>
  name === 'panel' || (name !== null && name.startsWith('panel-'));

const sheet = makeSheet(`
  :host { display: block; }

  .tablist {
    display: flex;
    align-items: stretch;
    gap: var(--caos-space-4);
    border-bottom: 1px solid var(--caos-color-border);
    max-width: 100%;
    overflow-x: auto;
    scrollbar-width: none;
  }
  .tablist::-webkit-scrollbar { display: none; }

  .tab {
    appearance: none;
    border: none;
    background: transparent;
    color: var(--caos-color-text-muted);
    /* Dragon WorkspaceTabs — mono, 11px, .12em, uppercase */
    font-family: var(--caos-font-mono);
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    line-height: 1;
    padding: var(--caos-space-3) 2px;
    /* the active-tab indicator: a 2px accent underline that overlaps the 1px rule */
    border-bottom: 2px solid transparent;
    margin-bottom: -1px;
    cursor: pointer;
    white-space: nowrap;
    transition: color 0.12s ease, border-color 0.12s ease;
  }
  .tab:hover:not([aria-selected="true"]):not(:disabled) { color: var(--caos-color-text); }
  .tab[aria-selected="true"] {
    /* active = neutral ink at weight 700 with the accent underline (not accent ink) */
    color: var(--caos-color-text);
    font-weight: 700;
    border-bottom-color: var(--caos-color-accent);
  }
  .tab:disabled { opacity: var(--caos-state-disabled-opacity); cursor: not-allowed; }
  .tab:focus-visible {
    outline: none;
    border-radius: var(--caos-radius-sm);
    box-shadow: var(--caos-focus-ring);
  }

  .panels { padding-top: var(--caos-space-4); }
  /* CAOS-1572 — a panel arrives in the shared slot or in its own tab's slot; both are panels.
     Written as two separate :not(:has()) rather than one with a list inside it, so each is exactly
     the construct that already shipped here — a selector an engine refuses is a rule it drops. */
  .panels:not(:has(::slotted([slot="panel"]))):not(:has(::slotted([slot^="panel-"]))) { display: none; }
  ::slotted([slot="panel"][hidden]),
  ::slotted([slot^="panel-"][hidden]) { display: none !important; }
`);

export class CaosTabStrip extends CaosElement {
  static get observedAttributes(): string[] {
    return ['value', 'label', 'aria-label'];
  }

  private tablist: HTMLDivElement;
  private panels: HTMLDivElement;
  private panelsSlot: HTMLSlotElement;
  /** CAOS-1572 — the slot each tab's own panels arrive in, keyed by the tab's value. */
  private readonly tabSlots = new Map<string, HTMLSlotElement>();
  private _items: CaosTabItem[] | null = null;
  private tabs: HTMLButtonElement[] = [];
  private readonly seq = ++uid;

  constructor() {
    super(sheet);
    this.tablist = document.createElement('div');
    this.tablist.className = 'tablist';
    this.tablist.setAttribute('part', 'tablist');
    this.tablist.setAttribute('role', 'tablist');
    this.tablist.addEventListener('keydown', (e) => this.onKeydown(e));

    this.panels = document.createElement('div');
    this.panels.className = 'panels';
    this.panels.setAttribute('part', 'panels');
    this.panelsSlot = document.createElement('slot');
    this.panelsSlot.name = 'panel';
    this.panelsSlot.addEventListener('slotchange', () => this.reflectActive());
    this.panels.append(this.panelsSlot);

    this.root.append(this.tablist, this.panels);
  }

  connectedCallback(): void {
    if (this._items === null) this.readLightDom();
    this.syncName();
    this.render();
  }

  attributeChangedCallback(name: string): void {
    // CAOS-648 — the name follows whichever attribute it is written in, whenever that changes.
    if (name === 'label' || name === 'aria-label') {
      this.syncName();
      return;
    }
    this.reflectActive();
  }

  /**
   * CAOS-1572 — the accessible name, read from `label` first and from `aria-label` after it.
   *
   * `label` exists because a stored placement carries a component's own inputs and nothing else. A
   * page assembled from metadata can set an input; it cannot set an element attribute no component
   * declares. So the name this control's meaning depends on — Scope, Period, Record facets — was
   * unsayable on a real page, while the documentation showed an example setting `aria-label` that a
   * page could not copy. Taking the name from a declared `label` is the shape `data-table.ts`
   * already uses for the grid, so the input is the library's existing answer rather than a second one.
   *
   * It goes one step further than the grid, and the step is deliberate rather than an oversight: the
   * grid reads `label` ALONE, while these two go on reading `aria-label` after it. The grid never
   * documented any other way to name itself; these two did, the workspace's own record page, shell
   * frame and component reference all set it, and every piece of hand-written markup reaches for it
   * first. `label` wins where both are present, because the declared input is the one a page can
   * author, so it is the one that decides.
   */
  private syncName(): void {
    applyAccessibleName(this.tablist, this.getAttribute('label') ?? this.getAttribute('aria-label'), 'Tabs');
  }

  /** The tabs (data in). */
  get items(): CaosTabItem[] {
    return this._items ?? [];
  }
  set items(list: Array<CaosTabItem | string>) {
    this._items = list.map((o) => (typeof o === 'string' ? { value: o, label: o } : o));
    this.render();
  }

  get value(): string {
    return this.getAttribute('value') ?? '';
  }
  set value(v: string) {
    this.setAttribute('value', v);
  }

  /** Read declarative `<button>` / `<option>` children (ignoring the panel slot). */
  private readLightDom(): void {
    const nodes = Array.from(this.children).filter(
      (c) => (c.tagName === 'BUTTON' || c.tagName === 'OPTION') && !isPanelSlot(c.getAttribute('slot')),
    );
    if (nodes.length) {
      this._items = nodes.map((n) => ({
        value: (n as HTMLOptionElement).value || (n.textContent ?? '').trim(),
        label: (n.textContent ?? '').trim(),
        disabled: n.hasAttribute('disabled'),
      }));
    }
  }

  private render(): void {
    const items = this._items ?? [];
    this.tablist.replaceChildren();
    this.tabs = items.map((it, i) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'tab';
      btn.setAttribute('part', 'tab');
      btn.setAttribute('role', 'tab');
      btn.id = `caos-tab-${this.seq}-${i}`;
      btn.dataset.value = it.value;
      btn.textContent = it.label;
      if (it.disabled) btn.disabled = true;
      btn.addEventListener('click', () => {
        if (!btn.disabled) this.select(it.value, true);
      });
      this.tablist.append(btn);
      return btn;
    });
    this.syncTabSlots(items);
    // Default the active tab to the current value or the first enabled tab.
    if (!this.hasAttribute('value')) {
      const first = items.find((it) => !it.disabled) ?? items[0];
      if (first) this.setAttribute('value', first.value);
    }
    this.reflectActive();
  }

  /**
   * CAOS-1572 — a slot per tab, named after the tab, kept in step with the tabs themselves.
   *
   * The association between a tab and its panel had one expression, `data-value` on the slotted
   * panel, and that is an attribute set on the ELEMENT. A stored placement holds a component key,
   * the inputs that component declares, and the name of the slot the piece goes into — so a page
   * assembled from metadata could place the strip, place the panels, and had no way left to say
   * which went with which. The slot NAME is the one part of that shape which can carry it, which is
   * why the answer is a slot rather than another attribute: nothing else a placement holds could
   * say it, and no panel component could declare an input for it without every possible panel
   * component declaring the same one.
   *
   * `data-value` on the shared `panel` slot still works and is still documented. It is what markup
   * written by hand already says, the workspace's own record page among it, and removing it would
   * buy nothing this adds.
   */
  private syncTabSlots(items: readonly CaosTabItem[]): void {
    const wanted = new Set(items.map((it) => it.value));
    // The map's own keys ARE the values drawn a moment ago — a slot is made for every tab and only
    // ever removed here — so nothing has to be remembered separately to find a tab that has gone.
    for (const [value, slot] of this.tabSlots) {
      if (wanted.has(value)) continue;
      this.releasePanels(value);
      slot.remove();
      this.tabSlots.delete(value);
    }
    for (const it of items) {
      if (this.tabSlots.has(it.value)) continue;
      const slot = document.createElement('slot');
      slot.name = panelSlotName(it.value);
      slot.addEventListener('slotchange', () => this.reflectActive());
      this.panels.append(slot);
      this.tabSlots.set(it.value, slot);
    }
  }

  /**
   * Everything offered as a panel for one tab, by either route, in the order the two are read.
   *
   * A list rather than one element because a slot takes as many children as are put in it, and a
   * placement that fills a tab with three pieces means all three to be that tab's panel. Wiring
   * only the first would leave the other two visible under every tab.
   *
   * Read off the host's own children rather than off `slot.assignedElements()`, which is the same
   * set by a longer road: a named slot takes exactly the direct children whose `slot` attribute
   * names it. Asking the children directly makes the wiring independent of when the shadow tree
   * settles its assignment — a page builds a piece and its contents BEFORE putting it on the page,
   * which is the order `buildPlacement` and the page surface both use, and a panel wired only once
   * a later change happened to re-run this would be a panel with no role until something moved.
   */
  private panelsFor(value: string): HTMLElement[] {
    const own: HTMLElement[] = [];
    const byData: HTMLElement[] = [];
    const wanted = panelSlotName(value);
    for (const child of Array.from(this.children) as HTMLElement[]) {
      const slot = child.getAttribute('slot');
      if (slot === wanted) own.push(child);
      else if (slot === 'panel' && child.dataset.value === value) byData.push(child);
    }
    return [...own, ...byData];
  }

  /**
   * Give back everything this strip wrote onto the panels of a tab that no longer exists.
   *
   * Removing the tab's slot stops the panel being DRAWN, and that is only half of it: the panel is
   * an element of the page's own, and the role, the label and the hidden flag written onto it would
   * otherwise stay there naming a tab that is gone — a `role="tabpanel"` whose `aria-labelledby`
   * points at nothing, which a screen reader reads as a broken relationship rather than as an
   * absent one. So a released panel goes back to how it arrived, which is also what this piece
   * documents for a panel that never matched a tab: left alone.
   *
   * Only what this strip wrote is taken back. A panel is released only if it is labelled by one of
   * THIS strip's own tabs, so an element a page gave a role of its own is not quietly stripped.
   *
   * The id is deliberately kept. It was minted as a HANDLE, and a stylesheet, a fragment link or a
   * page's own script may already point at it; taking it back would break those in order to tidy a
   * name nobody reads. For the same reason a panel moved from one tab's slot to another keeps the
   * id it was first given: it stays unique, `aria-labelledby` and `aria-controls` both repoint, and
   * only the text of the id still recalls where it started.
   */
  private releasePanels(value: string): void {
    const mine = `caos-tab-${this.seq}-`;
    for (const p of this.panelsFor(value)) {
      if (!(p.getAttribute('aria-labelledby') ?? '').startsWith(mine)) continue;
      p.removeAttribute('role');
      p.removeAttribute('aria-labelledby');
      p.removeAttribute('tabindex');
      p.removeAttribute('hidden');
    }
  }

  private reflectActive(): void {
    const active = this.getAttribute('value');
    this.tabs.forEach((btn) => {
      const isActive = btn.dataset.value === active;
      btn.setAttribute('aria-selected', String(isActive));
      btn.tabIndex = isActive ? 0 : -1;
      // associate the tab with its panels, if any
      const panels = this.panelsFor(btn.dataset.value ?? '');
      const ids: string[] = [];
      panels.forEach((p, index) => {
        // The first panel keeps the id this component has always minted for it; only a second and
        // further panel for one tab — which nothing could ask for before — needs distinguishing.
        if (!p.id) p.id = `caos-tabpanel-${this.seq}-${btn.dataset.value}${index === 0 ? '' : `-${index}`}`;
        p.setAttribute('role', 'tabpanel');
        p.setAttribute('aria-labelledby', btn.id);
        p.tabIndex = 0;
        if (isActive) p.removeAttribute('hidden');
        else p.setAttribute('hidden', '');
        ids.push(p.id);
      });
      // Removed rather than left when a tab has no panel: a tab that once had one and lost it
      // would otherwise keep pointing at an element that is no longer its panel.
      if (ids.length > 0) btn.setAttribute('aria-controls', ids.join(' '));
      else btn.removeAttribute('aria-controls');
    });
  }

  private select(value: string, focus: boolean): void {
    if (this.getAttribute('value') !== value) {
      this.setAttribute('value', value);
      this.dispatchEvent(new CustomEvent('change', { bubbles: true, composed: true, detail: { value } }));
    }
    if (focus) this.tabs.find((b) => b.dataset.value === value)?.focus();
  }

  private onKeydown(e: KeyboardEvent): void {
    const keys = ['ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
    if (!keys.includes(e.key) || !this.tabs.length) return;
    e.preventDefault();
    const enabled = this.tabs.filter((b) => !b.disabled);
    if (!enabled.length) return;
    const currentBtn = this.tabs.find((b) => b.dataset.value === this.getAttribute('value'));
    let idx = currentBtn ? enabled.indexOf(currentBtn) : 0;
    if (idx < 0) idx = 0;
    const last = enabled.length - 1;
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        idx = idx >= last ? 0 : idx + 1;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        idx = idx <= 0 ? last : idx - 1;
        break;
      case 'Home':
        idx = 0;
        break;
      case 'End':
        idx = last;
        break;
    }
    const value = enabled[idx]?.dataset.value;
    if (value !== undefined) this.select(value, true);
  }
}

define('caos-tab-strip', CaosTabStrip);

declare global {
  interface HTMLElementTagNameMap {
    'caos-tab-strip': CaosTabStrip;
  }
}
